import memoize from 'memoizee';
import config from '@/constants/config';
import { contract } from '../../contract';
import { contractAbis, isFactoryAbiDataLoaded } from '@/constants/abis';
import { sleepUntil } from '@/utils/Async';
import { chunkArr, arrayToHashmap } from '../../utils/helpers';
import getPricesForAssets from './getPricesForAssets';
import getPoolUsdFigure from './getPoolUsdFigure';
import { getAtricryptoInfo } from '../common';
import { getNonFactoPools, poolIds, getPoolGauges, isFactoryPoolDataLoaded } from '@/constants/pools';

const getBalances = memoize(async (userAddress) => {
  const nonFactoPools = getNonFactoPools();
  await sleepUntil(() => contract.multicall && isFactoryAbiDataLoaded() && isFactoryPoolDataLoaded());

  // { compound: -1, … }
  const initialBalancesValues = arrayToHashmap(nonFactoPools.map(({ id }) => [id, -1]));


  if (!userAddress) return { balances: [], sumBalances: 0 };
  contract.contracts.compound = contract;
  let calls = poolIds.flatMap((k) => [
    // balanceOf(address)
    [contractAbis[k].token_address, '0x70a08231000000000000000000000000' + userAddress.slice(2)],
    // get_virtual_price
    [contractAbis[k].swap_address, "0xbb7b8b80"],
  ]);

  if (config.blockchainId === 'ethereum') {
    calls.push([contractAbis.susdv2.sCurveRewards_address, '0x70a08231000000000000000000000000' + userAddress.slice(2)])
    calls.push([contractAbis.sbtc.sCurveRewards_address, '0x70a08231000000000000000000000000' + userAddress.slice(2)])
    calls.push([contractAbis.iearn.sCurveRewards_address, '0x70a08231000000000000000000000000' + userAddress.slice(2)])
  }

  const nonGaugeCallsLength = calls.length
  calls.push(...getPoolGauges().map((gauge) => [gauge, '0x70a08231000000000000000000000000' + userAddress.slice(2)]))
  let aggcalls = await contract.multicall.methods.aggregate(calls).call()
  let decoded = aggcalls[1].map((hex) => contract.web3.eth.abi.decodeParameter('uint256', hex))

  let decodedGaugeBalances = decoded.slice(nonGaugeCallsLength)
  decoded = decoded.slice(0, nonGaugeCallsLength);

  const balances = initialBalancesValues;
  const btcPrice = (await getPricesForAssets(['bitcoin']))['bitcoin'];

  let i = 0;
  const values = chunkArr(decoded, 2).slice(0, poolIds.length);
  for (const v of values) {
    const key = poolIds[i];
    const pool = nonFactoPools[i];
    const poolBalance = +v[0] * (+v[1]) / 1e36;
    balances[key] = await getPoolUsdFigure(poolBalance, pool);
    i += 1;
  }

  if (config.blockchainId === 'ethereum') {
    let len = decoded.length;
    balances.susdv2 += ((+decoded[len - 3] * decoded[9]) / 1e36);
    balances.sbtc += (((+decoded[len - 2] * decoded[15]) / 1e36) * btcPrice);
    balances.iearn += ((+decoded[len - 1] * decoded[5]) / 1e36);
  } else if (config.blockchainId === 'polygon') {
    const atricryptoInfo = await getAtricryptoInfo(1, userAddress);
    balances.atricrypto = atricryptoInfo.totalUserContractBalancesUsd + atricryptoInfo.totalUserStakedContractBalancesUsd;
    const atricrypto2Info = await getAtricryptoInfo(2, userAddress);
    balances.atricrypto2 = atricrypto2Info.totalUserContractBalancesUsd + atricrypto2Info.totalUserStakedContractBalancesUsd;

    const atricrypto3Info = await getAtricryptoInfo(3, userAddress);
    console.log(atricrypto3Info, 'atricrypto3Infoatricrypto3Info')
    balances.atricrypto3 = atricrypto3Info.totalUserContractBalancesUsd + atricrypto3Info.totalUserStakedContractBalancesUsd;
    //console.log()


    console.log({
      atricryptoInfo,
      atricrypto2Info,
      atricrypto3Info,
      balances
    })
  } else if (config.blockchainId === 'avalanche') {
    const atricryptoInfo = await getAtricryptoInfo(1, userAddress);
    balances.atricrypto = atricryptoInfo.totalUserContractBalancesUsd + atricryptoInfo.totalUserStakedContractBalancesUsd;
    console.log({
      atricryptoInfo,
      balances
    })
  } else if (config.blockchainId === 'harmony') {
    const atricryptoInfo = await getAtricryptoInfo(1, userAddress);
    balances.atricrypto = atricryptoInfo.totalUserContractBalancesUsd + atricryptoInfo.totalUserStakedContractBalancesUsd;
    console.log({
      atricryptoInfo,
      balances
    })
  }

  i = 0;
  for (const pool of nonFactoPools) {
    if (pool.id === 'atricrypto' || pool.id === 'atricrypto2' || pool.id === 'atricrypto3') continue; // Handled above with totalUserStakedContractBalancesUsd
    let gaugeBalance;

    if (pool.addresses.gauge) {
      let callsGauges = calls.slice(nonGaugeCallsLength)
      gaugeBalance = +decodedGaugeBalances[callsGauges.findIndex((callGauge) => callGauge[0] == pool.addresses.gauge)] * (decoded[(i * 2) + 1] / 1e18)
    } else {
      gaugeBalance = 0; // Some pools don't have a gauge, hence balance in the gauge is empty
    }

    balances[pool.id] += await getPoolUsdFigure((gaugeBalance / 1e18), pool);
    i += 1;
  }

  return {
    balances,
    sumBalances: Object.values(balances || {}).reduce((a, b) => a + b, 0),
  };
}, {
  promise: true,
  maxAge: 10 * 1000, // 10s
});

export default getBalances;
